/* eslint-disable complexity */
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { forIE, forTablet } from '@westwing/ui-kit/breakPoints';
import {
    QuantityBadge as UiKitQuantityBadge,
    BadgesGrid,
    BadgesBottomLeft,
    BadgesBottomRight,
    BadgesTopLeft,
    BadgesTopRight,
} from '@westwing/ui-kit/ProductBadges';
import { ProductInterface } from '../../redux/products/types';
import useFeatureToggle from '../../hooks/useFeatureToggle';
import StateInterface from '../../redux/types';
import { Positions } from '../../../common/types/remoteConfig';
import { ProductSalesType, SaleBadge } from './SaleBadge';
import { ConnectedWishlistBadge } from './WishlistBadge';
import { SustainabilityBadge } from './TextBadges';
import { FeatureToggles } from 'Common/appshell_types/featureToggles';

const SustainabilityBadgeWrapper = styled.div`
    position: relative;
`;

const StyledSustainabilityBadge = styled(SustainabilityBadge)`
    margin-bottom: 0;
    position: absolute;
    bottom: -18px;
    left: 3px;

    background: ${({ theme }) => theme.colors.white};
    font-size: 10px;
    line-height: 16px;
    height: 21px;

    ${forTablet`
        left: -3px;
        bottom: -17px;
    `}
`;

const ProductBadgesGrid = styled(BadgesGrid)`
    grid-column: 1/4;
    grid-row: 1/3;
    width: 100%;
    height: 100%;
    ${forIE`
        -ms-grid-row : 1;
        -ms-grid-row-span : 2;
        -ms-grid-column : 1;
        -ms-grid-column-span : 3;
    `}
`;

export const ProductQuantityBadge: React.FC<{ quantity: number; className?: string }> = props => (
    <UiKitQuantityBadge {...props} data-testid="quantity-badge" />
);

const isProductDiscounted = (product: ProductInterface, featureToggles: FeatureToggles): boolean | undefined => {
    if (featureToggles) {
        const { blackFriday, cyberMonday } = product;
        const isBlackFriday = blackFriday && featureToggles.isBlackFridayActive;
        const isCyberMonday = cyberMonday && featureToggles.isCyberMondayActive;
        return (isBlackFriday || isCyberMonday) && featureToggles.isSpecialSalesEnabled;
    }
    return false;
};

export interface BadgeSlots {
    [Positions.bottomleft]: React.ReactNode[];
    [Positions.bottomright]: React.ReactNode[];
    [Positions.topleft]: React.ReactNode[];
    [Positions.topright]: React.ReactNode[];
}

interface ConnectedProductBadgesProps {
    product: ProductInterface;
}

const ConnectedProductBadges = ({ product }: ConnectedProductBadgesProps) => {
    const featureToggles = useFeatureToggle();
    const isDiscounted = isProductDiscounted(product, featureToggles);
    const { blackFriday, cyberMonday } = product;
    const isBlackFriday = blackFriday && featureToggles.isBlackFridayActive;
    const isCyberMonday = cyberMonday && featureToggles.isCyberMondayActive;
    const showSustainabilityBadge = product.isSustainable && featureToggles.p100Sustainability;

    const isImageServiceProductImageEnabled = useSelector<StateInterface, boolean>(
        ({ configurations }) => configurations.isImageServiceProductImageEnabled
    );
    const quantityBadgePosition = useSelector<StateInterface, Positions>(({ configurations }) => {
        return configurations.isImageServiceProductImageEnabled
            ? configurations.quantityBadgePosition
            : Positions.topleft;
    });

    const badgeSlots: BadgeSlots = {
        bottomleft: [],
        bottomright: [],
        topleft: [],
        topright: [],
    };

    badgeSlots[Positions.topright].push(<ConnectedWishlistBadge key="wishlist-icon" product={product} />);
    if (showSustainabilityBadge) {
        badgeSlots[Positions.bottomleft].push(
            <SustainabilityBadgeWrapper key="sustainability-badge">
                <StyledSustainabilityBadge />
            </SustainabilityBadgeWrapper>
        );
    }

    if (isDiscounted) {
        let productSalesType = ProductSalesType.NO_SALES;
        if (isBlackFriday) {
            productSalesType = ProductSalesType.BLACK_FRIDAY;
        }
        if (isCyberMonday && !isBlackFriday) {
            productSalesType = ProductSalesType.CYBER_MONDAY;
        }
        badgeSlots[Positions.topleft].push(
            <SaleBadge key="sale-badge" data-testid="sale-badge" productSalesTypes={productSalesType} />
        );
    }

    if (isImageServiceProductImageEnabled && product.quantityBadge > 0) {
        badgeSlots[quantityBadgePosition].push(
            <ProductQuantityBadge key="quantity-badge" quantity={product.quantityBadge} />
        );
    }

    return (
        <ProductBadgesGrid>
            <BadgesTopLeft>{badgeSlots[Positions.topleft]}</BadgesTopLeft>
            <BadgesTopRight>{badgeSlots[Positions.topright]}</BadgesTopRight>
            <BadgesBottomLeft>{badgeSlots[Positions.bottomleft]}</BadgesBottomLeft>
            <BadgesBottomRight>{badgeSlots[Positions.bottomright]}</BadgesBottomRight>
        </ProductBadgesGrid>
    );
};

export default ConnectedProductBadges;
